import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../utils/cn";
import { Dropdown } from "./Dropdown";

type TabItem = {
  id: string;
  label: string;
  disabled?: boolean;
};

type TabsProps = {
  tabs: TabItem[];
  activeId?: string;
  isLoading?: boolean;
  onChange?: (id: string) => void;
  variant?: "pill" | "underline";
  maxVisible?: number;
  overflowLabel?: string;
};

export function Tabs({
  tabs,
  activeId,
  isLoading = false,
  onChange,
  variant = "pill",
  maxVisible,
  overflowLabel = "Plus…",
}: TabsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const overflowMeasureRef = useRef<HTMLButtonElement | null>(null);
  const tabMeasureRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [autoVisibleCount, setAutoVisibleCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (maxVisible !== undefined) return;
    const computeVisible = () => {
      const containerWidth = containerRef.current?.offsetWidth ?? 0;
      if (!containerWidth) return;
      const widths = tabMeasureRefs.current.map((el) => el?.offsetWidth ?? 0);
      const overflowWidth = overflowMeasureRef.current?.offsetWidth ?? 0;
      const totalWidth = widths.reduce((sum, width) => sum + width, 0);
      if (totalWidth <= containerWidth) {
        setAutoVisibleCount(tabs.length);
        return;
      }
      let used = 0;
      let count = 0;
      for (const width of widths) {
        if (used + width + overflowWidth > containerWidth) break;
        used += width;
        count += 1;
      }
      setAutoVisibleCount(Math.max(1, count));
    };
    computeVisible();
    return () => {};
  }, [maxVisible, tabs.length]);

  useEffect(() => {
    if (maxVisible !== undefined) return;
    const onResize = () => {
      const containerWidth = containerRef.current?.offsetWidth ?? 0;
      if (!containerWidth) return;
      const widths = tabMeasureRefs.current.map((el) => el?.offsetWidth ?? 0);
      const overflowWidth = overflowMeasureRef.current?.offsetWidth ?? 0;
      const totalWidth = widths.reduce((sum, width) => sum + width, 0);
      if (totalWidth <= containerWidth) {
        setAutoVisibleCount(tabs.length);
        return;
      }
      let used = 0;
      let count = 0;
      for (const width of widths) {
        if (used + width + overflowWidth > containerWidth) break;
        used += width;
        count += 1;
      }
      setAutoVisibleCount(Math.max(1, count));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [maxVisible, tabs.length]);
  if (isLoading) {
    return (
      <div className="inline-flex rounded-md border border-border bg-surface p-1">
        <div className="h-7 w-20 rounded-md bg-surface-muted" />
        <div className="ml-2 h-7 w-20 rounded-md bg-surface-muted" />
        <div className="ml-2 h-7 w-20 rounded-md bg-surface-muted" />
      </div>
    );
  }
  const computedMax = maxVisible ?? autoVisibleCount ?? tabs.length;
  const visibleTabs = tabs.length > computedMax ? tabs.slice(0, computedMax) : tabs;
  const overflowTabs = tabs.length > computedMax ? tabs.slice(computedMax) : [];
  if (variant === "underline") {
    return (
      <div ref={containerRef} className="flex items-center gap-4 border-b border-border">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={cn(
              "pb-2 text-sm border-b-2 transition-all duration-150 ease-out",
              tab.id === activeId ? "text-text border-primary" : "text-muted border-transparent hover:text-text",
              tab.disabled && "opacity-50 pointer-events-none"
            )}
            onClick={() => onChange?.(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        {overflowTabs.length ? (
          <Dropdown
            label={overflowLabel}
            align="right"
            items={overflowTabs.map((tab) => ({
              label: tab.label,
              onClick: () => onChange?.(tab.id),
              disabled: tab.disabled,
            }))}
          />
        ) : null}
        <div ref={measureRef} className="absolute invisible -z-10 h-0 w-0 overflow-hidden">
          {tabs.map((tab, index) => (
            <button
              key={`measure-${tab.id}`}
              ref={(el) => {
                tabMeasureRefs.current[index] = el;
              }}
              type="button"
              className="pb-2 text-sm border-b-2"
            >
              {tab.label}
            </button>
          ))}
          <button ref={overflowMeasureRef} type="button" className="text-sm">
            {overflowLabel}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div ref={containerRef} className="inline-flex rounded-md border border-border bg-surface p-1">
      {visibleTabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={cn(
            "rounded-md px-3 py-1 text-sm text-text hover:bg-surface-muted",
            tab.id === activeId && "bg-surface-muted",
            tab.disabled && "opacity-50 pointer-events-none"
          )}
          onClick={() => onChange?.(tab.id)}
        >
          {tab.label}
        </button>
      ))}
      {overflowTabs.length ? (
        <Dropdown
          label={overflowLabel}
          align="right"
          items={overflowTabs.map((tab) => ({
            label: tab.label,
            onClick: () => onChange?.(tab.id),
            disabled: tab.disabled,
          }))}
        />
      ) : null}
      <div ref={measureRef} className="absolute invisible -z-10 h-0 w-0 overflow-hidden">
        {tabs.map((tab, index) => (
          <button
            key={`measure-${tab.id}`}
            ref={(el) => {
              tabMeasureRefs.current[index] = el;
            }}
            type="button"
            className="rounded-md px-3 py-1 text-sm"
          >
            {tab.label}
          </button>
        ))}
        <button ref={overflowMeasureRef} type="button" className="text-sm">
          {overflowLabel}
        </button>
      </div>
    </div>
  );
}
